import React, { useEffect, useState, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Target, TrendingUp, Save, IndianRupee, Users, Search, MapPin, ChevronDown, ChevronRight, Edit3, Download, Store } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/utils/api';

const DIVISIONS = ["Arvind Tresca DTR", "Arvind Tresca WS", "GG DTR", "GG WS", "TAS"];
const ALL_REGIONS = ["Jharkhand", "Bihar", "West Bengal", "Odisha", "Assam", "Tripura", "Meghalaya"];
const GG_REGIONS = ["Bihar", "West Bengal"];
const getRegions = (division) => (division === "GG DTR" || division === "GG WS") ? GG_REGIONS : ALL_REGIONS;
const MONTH_LABELS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800 border-green-200',
  dormant: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  blocked: 'bg-orange-100 text-orange-800 border-orange-300',
  closed: 'bg-red-100 text-red-800 border-red-200',
};

const fmtNum = (n) => {
  if (!n) return '0';
  if (n >= 10000000) return `${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(2)} L`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
};

const pctColor = (pct) => {
  if (pct == null) return 'text-gray-400';
  if (pct >= 100) return 'text-green-600';
  if (pct >= 75) return 'text-yellow-600';
  return 'text-red-600';
};

// Achievement heat scale for quarter cells — green (>=100) -> lime (80-99)
// -> amber (60-79) -> red (<60). `strong` is the bold % cell fill, `soft`
// the lighter tint applied to the quarter's Actual cell so the whole
// quarter reads as a band. Returns empty strings for null (no target) so
// the cell stays neutral.
const heatBg = (pct) => {
  if (pct == null) return { strong: '', soft: '' };
  if (pct >= 100) return { strong: 'bg-green-500 text-white', soft: 'bg-green-50' };
  if (pct >= 80) return { strong: 'bg-lime-300 text-lime-900', soft: 'bg-lime-50' };
  if (pct >= 60) return { strong: 'bg-amber-300 text-amber-900', soft: 'bg-amber-50' };
  return { strong: 'bg-red-400 text-white', soft: 'bg-red-50' };
};

export default function SalesTargets() {
  const { user, selectedDivision } = useOutletContext();
  const now = new Date();
  const defaultFY = now.getMonth() >= 3
    ? `${now.getFullYear()}-${String(now.getFullYear() + 1).slice(2)}`
    : `${now.getFullYear() - 1}-${String(now.getFullYear()).slice(2)}`;

  const activeDivision = selectedDivision && selectedDivision !== 'all' ? selectedDivision : 'GG DTR';
  const REGIONS = getRegions(activeDivision);
  const [targetsFY, setTargetsFY] = useState(defaultFY);
  const isAdmin = user?.role === 'admin' || user?.role === 'accounts';
  // Sales target editing is restricted to admin + accounts roles only. Every
  // other role (sales_member, etc.) can view but never enter/edit targets.
  // The backend enforces the same rule on /sales-targets/bulk,
  // /sales-targets/customer-targets and DELETE /sales-targets.
  const canEditTargets = user?.role === 'admin' || user?.role === 'accounts';
  // Viewing targets (read-only) is open to every role that can reach this
  // RBAC-gated page — including TAS-authorised sales_members like Sahil,
  // who should see the target columns next to their actuals but never
  // edit them. Only the edit affordances (Save buttons, pencil editor)
  // stay gated on canEditTargets.
  const canViewTargets = true;
  const [viewMode, setViewMode] = useState('region_targets');

  // Region targets state — keyed by region
  const [regionTargets, setRegionTargets] = useState({}); // { region: [{period, target_orders_units, target_dispatch_value}, ...] }
  const [activeRegion, setActiveRegion] = useState(REGIONS[0]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [targetsLoadOk, setTargetsLoadOk] = useState(true);
  const [savingTargets, setSavingTargets] = useState(false);

  // Performance state
  const [performanceData, setPerformanceData] = useState(null);
  // Month-wise region targets vs customer-wise targets, reconciled per region
  // by the backend. Refreshed whenever either side is saved.
  const [consistency, setConsistency] = useState(null);
  const [loadingPerf, setLoadingPerf] = useState(false);

  // Customer state
  const [customerData, setCustomerData] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [custSearch, setCustSearch] = useState('');
  const [custStatusFilter, setCustStatusFilter] = useState('all');
  const [custEmployeeFilter, setCustEmployeeFilter] = useState('all');
  const [custRegionFilter, setCustRegionFilter] = useState('all');
  const [custSort, setCustSort] = useState({ field: 'dispatch_value', dir: 'desc' });
  const [savingCustTargets, setSavingCustTargets] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [downloadingCustomerId, setDownloadingCustomerId] = useState(null);
  const [expandedRegion, setExpandedRegion] = useState(null);  // region name or null
  const [expandedCustomer, setExpandedCustomer] = useState(null);  // customer name or null
  // Track which customer rows have unsaved target edits so we can surface a prominent Save CTA.
  const [dirtyCustomerCodes, setDirtyCustomerCodes] = useState(new Set());

  // Edit-target dialog state. Targets are sacrosanct (drive incentive
  // calculations) so we replaced the inline-editable spinner inputs
  // with read-only ₹ text and force admins to open this dialog for
  // any change — prevents accidental wheel-scroll / arrow-key edits.
  const [targetEditOpen, setTargetEditOpen] = useState(false);
  const [targetEditRowIdx, setTargetEditRowIdx] = useState(-1);
  // Local draft: { annual: number, quarters: { Q1, Q2, Q3, Q4 } }
  const [targetEditDraft, setTargetEditDraft] = useState({ annual: 0, quarters: { Q1: 0, Q2: 0, Q3: 0, Q4: 0 } });
  // Store targets (TAS). A customer row carries `stores` when its SAP code
  // has ≥ 2 ship-to locations or store targets already exist. The editor
  // then gets a "By store" section: { [ship_to_code]: { Q1, Q2, Q3, Q4 } }.
  // null = no store section (today's single-figure flow). When any store
  // figure is non-zero, Save posts /sales-targets/customer-store-targets
  // and the backend writes the SUM into the code's target — so the code
  // figures above are only a check, never a second source of truth.
  const [storeEditDraft, setStoreEditDraft] = useState(null);
  const [savingStoreTargets, setSavingStoreTargets] = useState(false);
  // Which customers have their store rows unfolded under them (by code).
  const [expandedStores, setExpandedStores] = useState(() => new Set());

  const getFYMonths = () => {
    const yr = parseInt(targetsFY.slice(0, 4));
    return Array.from({ length: 12 }, (_, i) => {
      const m = 4 + i;
      const y = m <= 12 ? yr : yr + 1;
      const mm = m <= 12 ? m : m - 12;
      return `${y}-${String(mm).padStart(2, '0')}`;
    });
  };

  // Which division/FY the grid on screen actually belongs to. A fast
  // division switch let an older response land after a newer one, and Save
  // then wrote one division's numbers under another (audit F-44).
  const targetsReqRef = useRef('');
  const [targetsLoadedFor, setTargetsLoadedFor] = useState('');

  // Fetch region targets for all regions
  const fetchRegionTargets = async () => {
    const key = `${activeDivision}|${targetsFY}`;
    targetsReqRef.current = key;
    setLoadingTargets(true);
    setTargetsLoadedFor('');
    try {
      setTargetsLoadOk(true);
      const res = await axios.get(`${API}/sales-targets?division=${activeDivision}&fy=${targetsFY}`, { withCredentials: true });
      if (targetsReqRef.current !== key) return; // a newer division/FY was chosen meanwhile
      const fyMonths = getFYMonths();
      const byRegion = {};
      REGIONS.forEach(r => {
        byRegion[r] = fyMonths.map(p => ({ period: p, target_orders_units: 0, target_dispatch_value: 0 }));
      });
      (res.data.targets || []).forEach(t => {
        const r = t.region;
        if (r && byRegion[r]) {
          const idx = fyMonths.indexOf(t.period);
          if (idx >= 0) {
            byRegion[r][idx].target_orders_units = t.target_orders_units || 0;
            byRegion[r][idx].target_dispatch_value = t.target_dispatch_value || 0;
          }
        }
      });
      setRegionTargets(byRegion);
      setTargetsLoadedFor(key);
    } catch (err) {
      if (targetsReqRef.current !== key) return;
      console.error('Failed to fetch targets:', err);
      setTargetsLoadOk(false);
      toast.error('Could not load targets — showing nothing rather than stale figures. Refresh before editing or saving.');
    } finally {
      if (targetsReqRef.current === key) setLoadingTargets(false);
    }
  };

  const saveRegionTargets = async () => {
    if (targetsLoadedFor !== `${activeDivision}|${targetsFY}`) {
      toast.error('Targets for this division are still loading — wait a moment, then save.');
      return;
    }
    if (!targetsLoadOk) {
      toast.error('Targets failed to load — refusing to save so existing targets are not overwritten with empty values. Refresh and try again.');
      return;
    }
    setSavingTargets(true);
    try {
      const targets = [];
      REGIONS.forEach(region => {
        (regionTargets[region] || []).forEach(row => {
          targets.push({ division: activeDivision, region, period: row.period, target_orders_units: row.target_orders_units, target_dispatch_value: row.target_dispatch_value });
        });
      });
      await axios.post(`${API}/sales-targets/bulk`, { targets }, { withCredentials: true });
      toast.success(`Region targets saved for ${activeDivision} (${targetsFY})`);
      fetchPerformance();
      fetchConsistency().then(warnIfInconsistent);
    } catch (err) {
      toast.error('Failed to save targets');
    } finally {
      setSavingTargets(false);
    }
  };

  const fetchPerformance = async () => {
    setLoadingPerf(true);
    try {
      const res = await axios.get(`${API}/sales-targets/performance?division=${activeDivision}&fy=${targetsFY}`, { withCredentials: true });
      setPerformanceData(res.data.performance?.[0] || null);
    } catch (err) {
      console.error('Failed to fetch performance:', err);
      toast.error('Could not load target performance — figures may be missing.');
    } finally {
      setLoadingPerf(false);
    }
  };

  const fetchConsistency = async () => {
    try {
      const res = await axios.get(`${API}/sales-targets/consistency?division=${activeDivision}&fy=${targetsFY}`, { withCredentials: true });
      setConsistency(res.data);
      return res.data;
    } catch (err) {
      console.error('Failed to reconcile targets:', err);
      setConsistency(null);
      return null;
    }
  };

  // Shown after either sheet is saved so the person editing learns of a gap
  // right away, not on the next visit.
  const warnIfInconsistent = (data) => {
    if (data && data.has_targets && !data.ok) {
      const names = data.regions.filter(r => r.mismatch).map(r => r.region);
      if (data.unassigned?.customer_count) names.push('no region');
      toast.warning(`Customer-wise and month-wise targets don't match (${names.join(', ')}) — see the reconciliation panel.`);
    }
  };

  const fetchCustomerPerformance = async () => {
    setLoadingCustomers(true);
    try {
      const res = await axios.get(`${API}/sales-targets/customer-performance?division=${activeDivision}&fy=${targetsFY}`, { withCredentials: true });
      setCustomerData(res.data.customers || []);
      setDirtyCustomerCodes(new Set());
    } catch (err) {
      console.error('Failed to fetch customer performance:', err);
      toast.error('Could not load customer performance — refresh before editing customer targets.');
    } finally {
      setLoadingCustomers(false);
    }
  };

  const downloadCustomerReport = async () => {
    setDownloadingReport(true);
    try {
      const res = await axios.get(
        `${API}/sales-targets/customer-report-pdf?division=${encodeURIComponent(activeDivision)}&fy=${targetsFY}&sort_by=${custSort.field}&sort_dir=${custSort.dir}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TargetReport_${activeDivision.replace(/\s+/g, '_')}_FY${targetsFY}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download target report:', err);
      toast.error('Could not generate the report PDF.');
    } finally {
      setDownloadingReport(false);
    }
  };

  // Per-customer focused statement — the copy a single customer asks for to
  // back a quarterly incentive claim.
  const downloadSingleCustomerReport = async (c) => {
    const key = c.customer_id || c.customer_code || c.name;
    setDownloadingCustomerId(key);
    try {
      const idParam = c.customer_id
        ? `&customer_id=${encodeURIComponent(c.customer_id)}`
        : `&customer_code=${encodeURIComponent(c.customer_code || '')}`;
      const res = await axios.get(
        `${API}/sales-targets/customer-report-pdf?division=${encodeURIComponent(activeDivision)}&fy=${targetsFY}${idParam}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TargetStatement_${(c.name || c.customer_code || 'customer').replace(/\s+/g, '_')}_FY${targetsFY}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download customer statement:', err);
      toast.error('Could not generate the customer statement PDF.');
    } finally {
      setDownloadingCustomerId(null);
    }
  };

  const saveCustomerTargets = async () => {
    // Save ONLY the customers currently visible on the page (respecting
    // search + status + employee filters). This keeps the Save action
    // scoped to what the admin is actively looking at — e.g. when an
    // employee is picked in the dropdown, Save applies to that employee's
    // customer list only, not every customer in the division.
    if (!filteredCustomers.length) {
      toast.info('No customers on the current page to save');
      return;
    }
    setSavingCustTargets(true);
    try {
      const isTas = activeDivision === 'TAS';
      const targets = filteredCustomers.map(c => {
        const base = {
          name: c.name,
          customer_code: c.customer_code,
          target_orders_units: c.target_orders_units || 0,
          target_dispatch_value: c.target_dispatch_value || 0,
        };
        // TAS — also send the quarterly breakdown so the backend
        // stores it on the document and keeps the annual mirror in
        // sync. Non-TAS divisions stay on the annual-only payload.
        if (isTas && Array.isArray(c.stored_quarterly)) {
          base.quarterly = c.stored_quarterly.map(q => ({
            quarter: q.quarter,
            target_dispatch_value: q.target_dispatch_value || 0,
            target_orders_units: q.target_orders_units || 0,
          }));
        }
        return base;
      });
      await axios.post(`${API}/sales-targets/customer-targets`, { division: activeDivision, fy: targetsFY, targets }, { withCredentials: true });
      toast.success(`Saved ${filteredCustomers.length} customer target(s)`);
      // Clear dirty markers for the codes we just persisted.
      const savedCodes = new Set(filteredCustomers.map(c => c.customer_code).filter(Boolean));
      setDirtyCustomerCodes(prev => {
        const next = new Set();
        prev.forEach(code => { if (!savedCodes.has(code)) next.add(code); });
        return next;
      });
      fetchCustomerPerformance();
      fetchConsistency().then(warnIfInconsistent);
    } catch (err) {
      toast.error('Failed to save customer targets');
    } finally {
      setSavingCustTargets(false);
    }
  };

  const updateRegionRow = (region, idx, field, value) => {
    setRegionTargets(prev => {
      const copy = { ...prev };
      const rows = [...(copy[region] || [])];
      rows[idx] = { ...rows[idx], [field]: parseFloat(value) || 0 };
      copy[region] = rows;
      return copy;
    });
  };

  const copyFirstToAllRegion = (region, field) => {
    setRegionTargets(prev => {
      const copy = { ...prev };
      const rows = [...(copy[region] || [])];
      if (rows.length === 0) return prev;
      const val = rows[0][field];
      copy[region] = rows.map(r => ({ ...r, [field]: val }));
      return copy;
    });
    toast.info(`Copied Apr value to all months for ${region}`);
  };

  const updateCustomerTarget = (idx, field, value) => {
    if (idx < 0) return;
    setCustomerData(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: parseFloat(value) || 0 };
      const code = copy[idx].customer_code;
      if (code) {
        setDirtyCustomerCodes(d => {
          const s = new Set(d);
          s.add(code);
          return s;
        });
      }
      return copy;
    });
  };

  // TAS only: edit a single quarter's target for a customer. Stored
  // shape on the row is `stored_quarterly` (what the API returns).
  // We mutate it locally and the backend will sum the four quarters
  // into the annual target_dispatch_value at save time.
  const updateCustomerQuarterTarget = (idx, quarter, value) => {
    if (idx < 0) return;
    setCustomerData(prev => {
      const copy = [...prev];
      const row = { ...copy[idx] };
      // Seed with all four quarters if not present yet
      const baseQuarters = ['Q1', 'Q2', 'Q3', 'Q4'].map(q => ({
        quarter: q, target_dispatch_value: 0, target_orders_units: 0,
      }));
      const existing = Array.isArray(row.stored_quarterly) && row.stored_quarterly.length
        ? baseQuarters.map(bq => {
            const found = row.stored_quarterly.find(x => (x.quarter || '').toUpperCase() === bq.quarter);
            return found ? { ...bq, ...found, quarter: bq.quarter } : bq;
          })
        : baseQuarters;
      const updated = existing.map(q =>
        q.quarter === quarter
          ? { ...q, target_dispatch_value: parseFloat(value) || 0 }
          : q
      );
      row.stored_quarterly = updated;
      // Keep the annual mirror in sync for downstream totals + display
      row.target_dispatch_value = updated.reduce((s, q) => s + (q.target_dispatch_value || 0), 0);
      copy[idx] = row;
      const code = row.customer_code;
      if (code) {
        setDirtyCustomerCodes(d => {
          const s = new Set(d);
          s.add(code);
          return s;
        });
      }
      return copy;
    });
  };

  // Helper: read a single quarter's stored target from a customer row.
  const getQuarterTarget = (customer, quarter) => {
    if (!Array.isArray(customer.stored_quarterly)) return 0;
    const q = customer.stored_quarterly.find(x => (x.quarter || '').toUpperCase() === quarter);
    return q ? (q.target_dispatch_value || 0) : 0;
  };

  // Open the edit-target dialog for a specific customer row. Reads
  // the current targets into a local draft so user can change /
  // cancel without mutating the underlying customerData.
  const openTargetEditor = (origIdx) => {
    if (origIdx < 0) return;
    const c = customerData[origIdx] || {};
    setTargetEditRowIdx(origIdx);
    setTargetEditDraft({
      annual: c.target_dispatch_value || 0,
      quarters: {
        Q1: getQuarterTarget(c, 'Q1'),
        Q2: getQuarterTarget(c, 'Q2'),
        Q3: getQuarterTarget(c, 'Q3'),
        Q4: getQuarterTarget(c, 'Q4'),
      },
    });
    // TAS customers with stores: seed one Q1–Q4 line per real store (the
    // "No store" bucket has no ship-to and cannot carry a target).
    const targetable = targetableStores(c);
    if (activeDivision === 'TAS' && targetable.length > 0) {
      const draft = {};
      targetable.forEach(s => {
        draft[s.ship_to_code] = {};
        ['Q1', 'Q2', 'Q3', 'Q4'].forEach(q => {
          const sq = Array.isArray(s.quarterly) ? s.quarterly.find(x => (x.quarter || '').toUpperCase() === q) : null;
          draft[s.ship_to_code][q] = sq ? (sq.target_dispatch_value || 0) : 0;
        });
      });
      setStoreEditDraft(draft);
    } else {
      setStoreEditDraft(null);
    }
    setTargetEditOpen(true);
  };

  // Stores a target can be set on: those with a ship-to code.
  const targetableStores = (c) => (Array.isArray(c?.stores) ? c.stores : []).filter(s => s && s.ship_to_code);
  // Σ of the store draft for one quarter.
  const storeDraftSum = (q) => Object.values(storeEditDraft || {}).reduce((s, row) => s + (row?.[q] || 0), 0);
  const storeDraftHasFigures = () => Object.values(storeEditDraft || {}).some(row => ['Q1', 'Q2', 'Q3', 'Q4'].some(q => (row?.[q] || 0) > 0));
  // Store targets already saved for this code (so an all-zero draft means
  // "clear them", which the server does by deleting the store rows).
  const storeTargetsOnFile = (c) => targetableStores(c).some(s => (s.target_dispatch_value || 0) > 0 || (s.target_orders_units || 0) > 0);

  // "Split by last period's share": divide each quarter's code figure across
  // the stores in proportion to their dispatch_value so far this FY (equal
  // split when nobody has dispatched). Rounded to whole rupees; the rounding
  // remainder lands on the largest share so Σ stores equals the code figure.
  const splitStoresByShare = () => {
    if (targetEditRowIdx < 0 || !storeEditDraft) return;
    const c = customerData[targetEditRowIdx] || {};
    const stores = targetableStores(c);
    if (!stores.length) return;
    const weights = stores.map(s => Math.max(0, s.dispatch_value || 0));
    const totalW = weights.reduce((a, b) => a + b, 0);
    const shares = totalW > 0 ? weights.map(w => w / totalW) : stores.map(() => 1 / stores.length);
    const biggest = shares.indexOf(Math.max(...shares));
    const next = {};
    stores.forEach(s => { next[s.ship_to_code] = {}; });
    ['Q1', 'Q2', 'Q3', 'Q4'].forEach(q => {
      const total = Math.round(targetEditDraft.quarters[q] || 0);
      let given = 0;
      stores.forEach((s, i) => {
        const v = i === biggest ? 0 : Math.floor(total * shares[i]);
        next[s.ship_to_code][q] = v;
        given += v;
      });
      next[stores[biggest].ship_to_code][q] = Math.max(0, total - given);
    });
    setStoreEditDraft(next);
    toast.info(totalW > 0 ? 'Split by each store\'s share of dispatches this FY' : 'No dispatches yet — split equally');
  };

  // Persist store targets straight away (unlike the code-level draft, which
  // queues for the page Save). The backend upserts the store rows, drops
  // any store sent all-zero, and writes the sum into the code's target.
  const saveStoreTargets = async () => {
    if (targetEditRowIdx < 0 || !storeEditDraft) return;
    const c = customerData[targetEditRowIdx] || {};
    if (!c.customer_code) { toast.error('This customer has no code for the division'); return; }
    setSavingStoreTargets(true);
    try {
      const stores = targetableStores(c).map(s => ({
        ship_to_code: s.ship_to_code,
        quarterly: ['Q1', 'Q2', 'Q3', 'Q4'].map(q => ({
          quarter: q,
          target_dispatch_value: storeEditDraft[s.ship_to_code]?.[q] || 0,
          target_orders_units: 0,
        })),
      }));
      await axios.post(`${API}/sales-targets/customer-store-targets`,
        { division: activeDivision, fy: targetsFY, customer_code: c.customer_code, stores },
        { withCredentials: true });
      const off = ['Q1', 'Q2', 'Q3', 'Q4'].filter(q => Math.abs(storeDraftSum(q) - (targetEditDraft.quarters[q] || 0)) > 1);
      toast.success(off.length
        ? `Store targets saved — the code target now equals the store total (${off.join(', ')} differed from the figure typed above)`
        : 'Store targets saved');
      setTargetEditOpen(false);
      setTargetEditRowIdx(-1);
      setStoreEditDraft(null);
      fetchCustomerPerformance();
      fetchPerformance();
      fetchConsistency().then(warnIfInconsistent);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save store targets');
    } finally {
      setSavingStoreTargets(false);
    }
  };

  // Commit the dialog draft into customerData. Uses the existing
  // mutators so the dirty-flag bookkeeping + annual mirror stays
  // consistent. After commit the row is dirty and Save (top of
  // page) will persist it via /sales-targets/customer-targets.
  const commitTargetEdit = () => {
    if (targetEditRowIdx < 0) return;
    if (activeDivision === 'TAS') {
      ['Q1', 'Q2', 'Q3', 'Q4'].forEach(q => {
        updateCustomerQuarterTarget(targetEditRowIdx, q, targetEditDraft.quarters[q]);
      });
    } else {
      updateCustomerTarget(targetEditRowIdx, 'target_dispatch_value', targetEditDraft.annual);
    }
    setTargetEditOpen(false);
    setTargetEditRowIdx(-1);
  };

  const updateCustomerStatus = async (customerId, newStatus) => {
    // Blocking needs a reason (overdues or any other issue) — the backend
    // refuses a blocked status without one.
    let reason = '';
    if (newStatus === 'blocked') {
      reason = (window.prompt('Reason for blocking this customer (e.g. overdues):') || '').trim();
      if (!reason) { toast.error('Blocking needs a reason'); return; }
    }
    try {
      await axios.put(`${API}/customers/${customerId}/status`, { status: newStatus, reason }, { withCredentials: true });
      toast.success(`Status updated to ${newStatus}`);
      setCustomerData(prev => prev.map(c => c.customer_id === customerId ? { ...c, status: newStatus } : c));
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const toggleSort = (field) => {
    setCustSort(prev => prev.field === field ? { field, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { field, dir: 'desc' });
  };

  useEffect(() => {
    // Reset active region when division changes (GG only has Bihar/WB)
    if (!REGIONS.includes(activeRegion)) setActiveRegion(REGIONS[0]);
    // Reset customer-wise region filter too — stale region (e.g. Jharkhand
    // selected under Arvind, then switched to GG) would hide everything.
    setCustRegionFilter('all');
    fetchRegionTargets();
    fetchPerformance();
    fetchConsistency();
  }, [activeDivision, targetsFY]);

  useEffect(() => {
    if (viewMode === 'customers') fetchCustomerPerformance();
  }, [viewMode, activeDivision, targetsFY]);

  const pf = performanceData?.fy_totals || {};
  const regionSummaries = performanceData?.region_summaries || {};

  // Current month data from monthly array
  const currentPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const currentMonthData = (performanceData?.monthly || []).find(m => m.period === currentPeriod) || {};
  const currentMonthLabel = MONTH_LABELS[(now.getMonth() - 3 + 12) % 12] || '';

  // YTD: sum of months up to and including current month
  const ytdData = (performanceData?.monthly || []).reduce((acc, m) => {
    if (m.period <= currentPeriod) {
      acc.target_dispatch_value += (m.target_dispatch_value || 0);
      acc.actual_dispatch_value += (m.actual_dispatch_value || 0);
    }
    return acc;
  }, { target_dispatch_value: 0, actual_dispatch_value: 0 });
  ytdData.pct = ytdData.target_dispatch_value > 0 ? Math.round(ytdData.actual_dispatch_value / ytdData.target_dispatch_value * 100 * 10) / 10 : null;
  ytdData.gap = ytdData.target_dispatch_value - ytdData.actual_dispatch_value;

  // Region totals for summary
  const allRegionTotals = {};
  REGIONS.forEach(r => {
    const rows = regionTargets[r] || [];
    allRegionTotals[r] = {
      target_orders_units: rows.reduce((s, x) => s + (x.target_orders_units || 0), 0),
      target_dispatch_value: rows.reduce((s, x) => s + (x.target_dispatch_value || 0), 0),
    };
  });
  const divisionTargetTotals = {
    target_orders_units: REGIONS.reduce((s, r) => s + allRegionTotals[r].target_orders_units, 0),
    target_dispatch_value: REGIONS.reduce((s, r) => s + allRegionTotals[r].target_dispatch_value, 0),
  };

  // Customer filters
  const employeeNames = [...new Set(customerData.map(c => c.assigned_employee_name || 'Head Office'))].sort();
  // Region filter list — use the division's canonical regions, plus any
  // unexpected regions already present in the data (e.g. "Unknown" or a
  // state that was bulk-assigned but isn't in the default list) so they
  // stay reachable from the filter dropdown.
  const customerRegionsSet = new Set(customerData.map(c => (c.region || '').trim()).filter(Boolean));
  REGIONS.forEach(r => customerRegionsSet.add(r));
  const customerRegionOptions = [...customerRegionsSet].sort();
  const filteredCustomers = customerData
    .filter(c => {
      if (custStatusFilter !== 'all' && c.status !== custStatusFilter) return false;
      if (custEmployeeFilter !== 'all' && (c.assigned_employee_name || 'Head Office') !== custEmployeeFilter) return false;
      if (custRegionFilter !== 'all' && (c.region || '').trim() !== custRegionFilter) return false;
      if (custSearch) {
        const q = custSearch.toLowerCase();
        return c.name.toLowerCase().includes(q) || (c.customer_code || '').toLowerCase().includes(q) || (c.region || '').toLowerCase().includes(q) || (c.assigned_employee_name || '').toLowerCase().includes(q);
      }
      return true;
    })
    .sort((a, b) => {
      // Group by status first: active accounts on top, then dormant, then
      // closed at the very end — regardless of the chosen sort within each
      // group.
      const statusRank = (s) => s === 'closed' ? 3 : s === 'dormant' ? 2 : s === 'blocked' ? 1 : 0;
      const ra = statusRank(a.status), rb = statusRank(b.status);
      if (ra !== rb) return ra - rb;
      // Achievement sort — customers with no target (null %) always sink
      // to the bottom regardless of direction, so the laggards-with-targets
      // are what surfaces at the end for gap planning.
      if (custSort.field === 'dispatch_pct') {
        const ap = a.dispatch_pct, bp = b.dispatch_pct;
        const aNull = ap == null, bNull = bp == null;
        if (aNull && bNull) return 0;
        if (aNull) return 1;
        if (bNull) return -1;
        return custSort.dir === 'desc' ? (bp - ap) : (ap - bp);
      }
      const mul = custSort.dir === 'desc' ? -1 : 1;
      if (custSort.field === 'name') return mul * a.name.localeCompare(b.name);
      return mul * ((a[custSort.field] || 0) - (b[custSort.field] || 0));
    });

  // Count of dirty edits that are ALSO visible in the current page — this is
  // what Save will actually persist. An edit made under a different filter
  // is still in customerData but won't be sent until it's visible again.
  const visibleDirtyCount = filteredCustomers.reduce((n, c) =>
    (c.customer_code && dirtyCustomerCodes.has(c.customer_code)) ? n + 1 : n, 0);

  const custTotals = filteredCustomers.reduce((acc, c) => ({
    dispatch_units: acc.dispatch_units + (c.dispatch_units || 0),
    dispatch_value: acc.dispatch_value + (c.dispatch_value || 0),
    target_orders_units: acc.target_orders_units + (c.target_orders_units || 0),
    target_dispatch_value: acc.target_dispatch_value + (c.target_dispatch_value || 0),
  }), { dispatch_units: 0, dispatch_value: 0, target_orders_units: 0, target_dispatch_value: 0 });

  // TAS quarter subtotals across the filtered page — for the footer
  // row under the quarter columns. {Q1: {target, actual}, Q2: ...}.
  const tasQuarterTotals = (() => {
    const base = { Q1: { target: 0, actual: 0 }, Q2: { target: 0, actual: 0 },
                   Q3: { target: 0, actual: 0 }, Q4: { target: 0, actual: 0 } };
    if (activeDivision !== 'TAS') return base;
    for (const c of filteredCustomers) {
      ['Q1', 'Q2', 'Q3', 'Q4'].forEach(q => {
        base[q].target += getQuarterTarget(c, q) || 0;
        const qa = Array.isArray(c.quarterly)
          ? (c.quarterly.find(x => (x.quarter || '').toUpperCase() === q) || {})
          : {};
        base[q].actual += qa.dispatch_value || 0;
      });
    }
    return base;
  })();

  // Which quarters of the selected FY are still in the future (relative to
  // today) — those quarter cells stay uncoloured. The current, in-progress
  // quarter IS coloured by its running achievement.
  const fyStartYear = parseInt(String(targetsFY).slice(0, 4), 10);
  const _qStart = {
    Q1: new Date(fyStartYear, 3, 1),
    Q2: new Date(fyStartYear, 6, 1),
    Q3: new Date(fyStartYear, 9, 1),
    Q4: new Date(fyStartYear + 1, 0, 1),
  };
  const isFutureQuarter = (q) => new Date() < _qStart[q];

  return (
    <div className="space-y-4 p-4 md:p-6 max-w-7xl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-heading font-bold flex items-center gap-2">
            <Target className="text-orange-600" size={24} />
            Sales Targets — {activeDivision}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Use the global division filter in the sidebar to switch divisions</p>
        </div>
        <Select value={targetsFY} onValueChange={setTargetsFY}>
          <SelectTrigger className="w-[130px] h-9 text-sm border-0 bg-gray-50 hover:bg-gray-100 font-semibold"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[-1, 0, 1].map(offset => {
              const y = new Date().getFullYear() + offset;
              const fy = `${y}-${String(y + 1).slice(2)}`;
              return <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Disclaimers — shown across every view and mirrored on every target
          PDF, so the caveats travel with the numbers before anything is
          shared externally. */}
      <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-xs text-amber-900">
        <p className="font-bold uppercase tracking-wide text-[10px] text-amber-700 mb-1">Important notes</p>
        <ol className="list-decimal list-inside space-y-0.5 font-medium">
          <li>Dispatch values are net of goods returns where return credit notes are on file.</li>
          <li>Please re-verify the data accuracy before sharing.</li>
        </ol>
      </div>

      {/* Reconciliation — the month-wise region sheet and the customer-wise
          sheet are planned separately; this is where they are compared. */}
      {consistency && consistency.has_targets && (
        consistency.ok ? (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-xs text-green-800 font-medium">
            Customer-wise and month-wise targets reconcile for {activeDivision} ({targetsFY}): {fmtNum(consistency.totals.customer_target_value)} on both sheets.
          </div>
        ) : (
          <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-2.5 text-xs text-red-900">
            <p className="font-bold uppercase tracking-wide text-[10px] text-red-700 mb-1">
              Targets don't reconcile — {consistency.mismatch_count} {consistency.mismatch_count === 1 ? 'gap' : 'gaps'}
            </p>
            <p className="mb-2 text-red-800">
              Customer-wise targets total <strong>{fmtNum(consistency.totals.customer_target_value)}</strong>, month-wise region targets total <strong>{fmtNum(consistency.totals.region_target_value)}</strong>. Revise whichever sheet is out of line.
            </p>
            <div className="overflow-x-auto">
              <table className="text-xs border-collapse min-w-[420px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-red-700">
                    <th className="text-left pr-4 pb-1">Region</th>
                    <th className="text-right pr-4 pb-1">Customer-wise</th>
                    <th className="text-right pr-4 pb-1">Month-wise</th>
                    <th className="text-right pb-1">Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {consistency.regions.map(r => (
                    <React.Fragment key={r.region}>
                      <tr className={r.mismatch ? 'font-semibold' : 'text-red-800/60'}>
                        <td className="pr-4 py-0.5">{r.region} <span className="font-normal opacity-70">({r.customer_count} cust.)</span></td>
                        <td className="text-right pr-4 py-0.5">{fmtNum(r.customer_target_value)}</td>
                        <td className="text-right pr-4 py-0.5">{fmtNum(r.region_target_value)}</td>
                        <td className="text-right py-0.5">{r.diff_value === 0 ? '—' : `${r.diff_value > 0 ? '+' : '−'}${fmtNum(Math.abs(r.diff_value))}`}</td>
                      </tr>
                      {r.mismatch && Array.isArray(r.quarters) && r.quarters.filter(q => q.mismatch).map(q => (
                        <tr key={`${r.region}-${q.quarter}`} className="text-red-800/80">
                          <td className="pr-4 py-0.5 pl-4">{q.quarter}</td>
                          <td className="text-right pr-4 py-0.5">{fmtNum(q.customer_target_value)}</td>
                          <td className="text-right pr-4 py-0.5">{fmtNum(q.region_target_value)}</td>
                          <td className="text-right py-0.5">{`${q.diff_value > 0 ? '+' : '−'}${fmtNum(Math.abs(q.diff_value))}`}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {consistency.unassigned?.customer_count > 0 && (
              <p className="mt-2 text-red-800">
                <strong>{fmtNum(consistency.unassigned.target_dispatch_value)}</strong> of customer targets sit on {consistency.unassigned.customer_count} customer(s) with no region on their profile, so they roll up nowhere:
                {' '}{consistency.unassigned.customers.slice(0, 6).map(u => u.name).join(', ')}{consistency.unassigned.customers.length > 6 ? ', …' : ''}. Set their region under Customers.
              </p>
            )}
            <p className="mt-1.5 text-[11px] text-red-700/80">Difference = customer-wise minus month-wise. Region comes from each customer's profile.</p>
          </div>
        )
      )}

      {/* Store targets vs code targets (TAS). The backend lists every code
          whose store targets do not add up to the code figure, per quarter
          (₹1 tolerance). Normally empty: saving store targets rewrites the
          code figure as their sum, so a gap means the code sheet was edited
          afterwards. Field names are read with fallbacks since the section
          is new. */}
      {(() => {
        const sec = consistency?.stores;
        const rows = Array.isArray(sec) ? sec : (Array.isArray(sec?.customers) ? sec.customers : (Array.isArray(sec?.mismatches) ? sec.mismatches : []));
        const offRows = rows.filter(r => r && (r.mismatch !== false) && (Array.isArray(r.quarters) ? r.quarters.length > 0 : true));
        if (!offRows.length) return null;
        const pick = (o, keys) => { for (const k of keys) { if (o && o[k] != null) return o[k]; } return 0; };
        return (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-xs text-amber-900">
            <p className="font-bold uppercase tracking-wide text-[10px] text-amber-700 mb-1 flex items-center gap-1">
              <Store size={11} /> Store targets don't add up to the code target — {offRows.length} {offRows.length === 1 ? 'customer' : 'customers'}
            </p>
            <div className="overflow-x-auto">
              <table className="text-xs border-collapse min-w-[420px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-amber-700">
                    <th className="text-left pr-4 pb-1">Customer</th>
                    <th className="text-right pr-4 pb-1">Σ stores</th>
                    <th className="text-right pr-4 pb-1">Code target</th>
                    <th className="text-right pb-1">Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {offRows.map(r => (
                    <React.Fragment key={r.customer_code || r.name}>
                      <tr className="font-semibold">
                        <td className="pr-4 py-0.5">{r.name || r.customer_name || r.customer_code} <span className="font-normal opacity-70">({r.customer_code})</span></td>
                        <td className="text-right pr-4 py-0.5">{fmtNum(pick(r, ['store_sum', 'stores_target_value', 'store_target_value', 'stores_value']))}</td>
                        <td className="text-right pr-4 py-0.5">{fmtNum(pick(r, ['code_value', 'code_target_value', 'customer_target_value', 'target_dispatch_value']))}</td>
                        <td className="text-right py-0.5">{(() => { const d = pick(r, ['diff', 'diff_value']); return d === 0 ? '—' : `${d > 0 ? '+' : '−'}${fmtNum(Math.abs(d))}`; })()}</td>
                      </tr>
                      {(Array.isArray(r.quarters) ? r.quarters : []).filter(q => q.mismatch !== false).map(q => (
                        <tr key={`${r.customer_code}-${q.quarter}`} className="text-amber-800/80">
                          <td className="pr-4 py-0.5 pl-4">{q.quarter}</td>
                          <td className="text-right pr-4 py-0.5">{fmtNum(pick(q, ['store_sum', 'stores_target_value', 'store_target_value', 'stores_value']))}</td>
                          <td className="text-right pr-4 py-0.5">{fmtNum(pick(q, ['code_value', 'code_target_value', 'customer_target_value', 'target_dispatch_value']))}</td>
                          <td className="text-right py-0.5">{(() => { const d = pick(q, ['diff', 'diff_value']); return `${d > 0 ? '+' : '−'}${fmtNum(Math.abs(d))}`; })()}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-1.5 text-[11px] text-amber-700/80">Difference = Σ store targets minus the code target. Re-save the store targets from the customer's pencil editor to bring the code back in line.</p>
          </div>
        );
      })()}

      {/* View Toggle — pill tabs */}
      <div className="inline-flex bg-muted/50 rounded-lg p-1 gap-0.5">
        {[
          { key: 'region_targets', icon: MapPin, label: 'Region Targets' },
          { key: 'performance', icon: TrendingUp, label: 'Monthly Performance' },
          { key: 'customers', icon: Users, label: 'Customer-wise' },
        ].map(({ key, icon: Icon, label }) => (
          <button key={key} onClick={() => setViewMode(key)}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md transition-all ${viewMode === key ? 'bg-white shadow-sm font-semibold text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {/* Summary Cards — Current Month + YTD + FY */}
      {performanceData && (
        <div className="space-y-3">
          {/* Division-level: Current Month, YTD, Full FY */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { label: `${currentMonthLabel} ${now.getFullYear()}`, sub: 'Current Month', color: 'blue', data: currentMonthData, pct: currentMonthData.dispatch_value_pct, target: currentMonthData.target_dispatch_value, actual: currentMonthData.actual_dispatch_value || 0 },
              { label: `Apr – ${currentMonthLabel}`, sub: 'Year to Date', color: 'green', data: ytdData, pct: ytdData.pct, target: ytdData.target_dispatch_value, actual: ytdData.actual_dispatch_value },
              { label: targetsFY, sub: 'Full Year', color: 'purple', data: pf, pct: pf.dispatch_value_pct, target: pf.target_dispatch_value, actual: pf.actual_dispatch_value || 0 },
            ].map(({ label, sub, color, pct, target, actual }) => (
              <div key={sub} className={`rounded-xl border border-${color}-200 p-4 bg-gradient-to-br from-${color}-50 to-white`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className={`text-[10px] font-bold text-${color}-600 uppercase tracking-wide`}>{sub}</p>
                    <p className="text-[10px] text-muted-foreground">{label}</p>
                  </div>
                  {target > 0 && <span className={`text-lg font-bold ${pctColor(pct)}`}>{pct}%</span>}
                </div>
                <p className="text-2xl font-bold">{fmtNum(actual)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Target: {fmtNum(target || 0)}</p>
                {target > 0 && (
                  <>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                      <div className={`h-1.5 rounded-full transition-all ${pct >= 100 ? 'bg-green-500' : pct >= 75 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${Math.min(pct || 0, 100)}%` }} />
                    </div>
                    <p className={`text-[10px] font-semibold mt-1 ${(target - actual) <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {(target - actual) <= 0 ? `${sub} Target Met!` : `Gap: ${fmtNum(target - actual)}`}
                    </p>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Region-wise Breakdown — click to expand monthly */}
          {Object.keys(regionSummaries).length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
              {Object.entries(regionSummaries).map(([region, rs]) => {
                const cm = rs.current_month || {};
                const yt = rs.ytd || {};
                const isExpanded = expandedRegion === region;
                return (
                  <div key={region} className={`rounded-xl border bg-white transition-shadow ${isExpanded ? 'shadow-md ring-1 ring-indigo-200 md:col-span-2 xl:col-span-3' : 'hover:shadow-sm'}`}>
                    <div className="p-3 cursor-pointer" onClick={() => setExpandedRegion(isExpanded ? null : region)}>
                      <div className="flex items-center justify-between mb-2.5">
                        <div className="flex items-center gap-1.5">
                          {isExpanded ? <ChevronDown size={13} className="text-indigo-500" /> : <ChevronRight size={13} className="text-indigo-500" />}
                          <MapPin size={13} className="text-indigo-500" />
                          <span className="text-xs font-bold text-gray-800 uppercase tracking-wide">{region}</span>
                        </div>
                        <span className="text-[9px] text-muted-foreground">click to {isExpanded ? 'collapse' : 'expand monthly'}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-1.5">
                        {[
                          { label: currentMonthLabel, bg: 'bg-blue-50', text: 'text-blue-600', d: cm },
                          { label: 'YTD', bg: 'bg-green-50', text: 'text-green-600', d: yt },
                          { label: 'FY', bg: 'bg-purple-50', text: 'text-purple-600', d: rs },
                        ].map(({ label: lbl, bg, text, d }) => (
                          <div key={lbl} className={`${bg} rounded-lg p-2 text-center`}>
                            <p className={`text-[8px] font-bold ${text} uppercase`}>{lbl}</p>
                            <p className="text-sm font-bold mt-0.5">{fmtNum(d.actual_dispatch_value || 0)}</p>
                            <p className="text-[8px] text-muted-foreground">/ {fmtNum(d.target_dispatch_value || 0)}</p>
                            {d.dispatch_value_pct != null && (
                              <div className="mt-1">
                                <span className={`text-[9px] font-bold ${pctColor(d.dispatch_value_pct)}`}>{d.dispatch_value_pct}%</span>
                                {d.target_dispatch_value > 0 && (
                                  <div className="w-full bg-gray-200 rounded-full h-1 mt-0.5">
                                    <div className={`h-1 rounded-full ${d.dispatch_value_pct >= 100 ? 'bg-green-500' : d.dispatch_value_pct >= 75 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${Math.min(d.dispatch_value_pct || 0, 100)}%` }} />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Expanded monthly breakdown */}
                    {isExpanded && rs.monthly && (
                      <div className="border-t px-3 pb-3">
                        <table className="w-full text-xs mt-2">
                          <thead>
                            <tr className="text-muted-foreground">
                              <th className="text-left py-1.5 font-medium">Month</th>
                              <th className="text-right py-1.5 font-medium">Target</th>
                              <th className="text-right py-1.5 font-medium">Actual</th>
                              <th className="text-right py-1.5 font-medium">%</th>
                              <th className="text-right py-1.5 font-medium">Gap</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rs.monthly.map((m, idx) => {
                              const gap = (m.target_dispatch_value || 0) - (m.actual_dispatch_value || 0);
                              return (
                                <tr key={m.period} className="border-t border-gray-100 hover:bg-gray-50">
                                  <td className="py-1.5 font-medium">{MONTH_LABELS[idx]} {m.period.slice(0, 4)}</td>
                                  <td className="py-1.5 text-right text-muted-foreground">{fmtNum(m.target_dispatch_value)}</td>
                                  <td className="py-1.5 text-right font-semibold">{fmtNum(m.actual_dispatch_value)}</td>
                                  <td className={`py-1.5 text-right font-bold ${pctColor(m.dispatch_value_pct)}`}>{m.dispatch_value_pct != null ? `${m.dispatch_value_pct}%` : '-'}</td>
                                  <td className={`py-1.5 text-right ${gap <= 0 ? 'text-green-600' : 'text-red-600'}`}>{gap <= 0 ? '-' : fmtNum(gap)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* REGION TARGETS VIEW — editable for admin, read-only for others */}
      {viewMode === 'region_targets' && (
        <Card className="border-border/60 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <MapPin className="text-indigo-600" size={20} />
                Region-wise Monthly Targets — {activeDivision} ({targetsFY})
              </CardTitle>
              {canEditTargets && (
                <Button onClick={saveRegionTargets} disabled={savingTargets || loadingTargets} className="bg-green-600 hover:bg-green-700 gap-1">
                  <Save size={14} /> {savingTargets ? 'Saving...' : 'Save All Regions'}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Region tabs */}
            <div className="inline-flex bg-gray-100 rounded-lg p-0.5 gap-0.5 mb-3">
              {REGIONS.map(r => (
                <button key={r} onClick={() => setActiveRegion(r)}
                  className={`text-xs px-3 py-1.5 rounded-md transition-all ${activeRegion === r ? 'bg-white shadow-sm font-semibold text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                  {r}
                  {allRegionTotals[r]?.target_dispatch_value > 0 && (
                    <span className="ml-1 text-[9px] opacity-60">({fmtNum(allRegionTotals[r].target_dispatch_value)})</span>
                  )}
                </button>
              ))}
            </div>

            {/* Region summary row */}
            <div className="mb-3 flex items-center gap-4 text-xs">
              <span className="bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-full font-semibold">Division: {fmtNum(divisionTargetTotals.target_dispatch_value)}</span>
              <span className="bg-gray-100 px-2.5 py-1 rounded-full">{activeRegion}: <strong>{fmtNum(allRegionTotals[activeRegion]?.target_dispatch_value || 0)}</strong></span>
              {(() => {
                const r = consistency?.regions?.find(x => x.region === activeRegion);
                if (!r || !r.customer_target_value) return null;
                // Compare against the figures being typed, not the saved
                // ones, so the pill turns green as the month-wise total is
                // brought in line — before Save.
                const live = allRegionTotals[activeRegion]?.target_dispatch_value || 0;
                const diff = r.customer_target_value - live;
                const off = Math.abs(diff) >= 1;
                return (
                  <span className={`px-2.5 py-1 rounded-full ${off ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}
                    title="Sum of this region's saved customer-wise targets, against the month-wise total above">
                    Customer-wise: <strong>{fmtNum(r.customer_target_value)}</strong>
                    {off && ` (${diff > 0 ? '+' : '−'}${fmtNum(Math.abs(diff))} vs month-wise)`}
                  </span>
                );
              })()}
            </div>

            {loadingTargets ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Loading...</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left p-3 font-semibold w-[120px]">Month</th>
                      <th className="text-right p-3 font-semibold">
                        <div className="flex items-center justify-end gap-2">
                          <IndianRupee size={14} className="text-green-600" /> Sales Target (₹)
                          {canEditTargets && (
                            <button className="text-blue-500 text-[10px] hover:underline font-normal" onClick={() => copyFirstToAllRegion(activeRegion, 'target_dispatch_value')}>Copy to all</button>
                          )}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(regionTargets[activeRegion] || []).map((row, idx) => (
                      <tr key={row.period} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium text-sm">{MONTH_LABELS[idx]} {row.period.slice(0, 4)}</td>
                        <td className="p-2">
                          {canEditTargets ? (
                            <Input type="number" className="text-right h-9 text-sm w-[180px] ml-auto"
                              value={row.target_dispatch_value || ''} onChange={e => updateRegionRow(activeRegion, idx, 'target_dispatch_value', e.target.value)} placeholder="0" />
                          ) : (
                            <p className="text-right text-sm font-medium pr-3">{row.target_dispatch_value ? fmtNum(row.target_dispatch_value) : '-'}</p>
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t-2 bg-gray-100 font-bold">
                      <td className="p-3">{activeRegion} FY Total</td>
                      <td className="p-3 text-right">{fmtNum(allRegionTotals[activeRegion]?.target_dispatch_value || 0)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* PERFORMANCE VIEW */}
      {viewMode === 'performance' && (
        <Card className="border-border/60 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="text-blue-600" size={20} />
              Monthly Performance — {activeDivision} ({targetsFY})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingPerf ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Loading...</p>
            ) : !performanceData ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No data available</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left p-2 font-semibold">Month</th>
                      <th className="text-right p-2 font-semibold text-green-700">Sales Target (₹)</th>
                      <th className="text-right p-2 font-semibold text-green-700">Sales Actual (₹)</th>
                      <th className="text-right p-2 font-semibold text-green-700">Achievement %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(performanceData.monthly || []).map((row, idx) => (
                      <tr key={row.period} className="border-b hover:bg-gray-50">
                        <td className="p-2 font-medium">{MONTH_LABELS[idx]} {row.period.slice(0, 4)}</td>
                        <td className="p-2 text-right text-muted-foreground">{fmtNum(row.target_dispatch_value || 0)}</td>
                        <td className="p-2 text-right font-semibold">{fmtNum(row.actual_dispatch_value || 0)}</td>
                        <td className={`p-2 text-right font-bold ${pctColor(row.dispatch_value_pct)}`}>{row.dispatch_value_pct != null ? `${row.dispatch_value_pct}%` : '-'}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 bg-gray-100 font-bold">
                      <td className="p-2">FY Total</td>
                      <td className="p-2 text-right">{fmtNum(pf.target_dispatch_value || 0)}</td>
                      <td className="p-2 text-right">{fmtNum(pf.actual_dispatch_value || 0)}</td>
                      <td className={`p-2 text-right ${pctColor(pf.dispatch_value_pct)}`}>{pf.dispatch_value_pct != null ? `${pf.dispatch_value_pct}%` : '-'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* CUSTOMER-WISE VIEW */}
      {viewMode === 'customers' && (
        <Card className="border-border/60 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Users className="text-indigo-600" size={20} />
                Customer-wise — {activeDivision} ({targetsFY})
              </CardTitle>
              <div className="flex gap-1.5 items-center flex-wrap">
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
                  <Input placeholder="Search customer..." className="pl-8 h-9 w-[170px] text-xs border-0 bg-gray-50 focus:bg-white rounded-lg" value={custSearch} onChange={e => setCustSearch(e.target.value)} />
                </div>
                <Select value={custStatusFilter} onValueChange={setCustStatusFilter}>
                  <SelectTrigger className="w-auto min-w-[90px] h-9 text-xs border-0 bg-gray-50 hover:bg-gray-100 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="dormant">Dormant</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                {/* Region filter — principals drive targets by region, so this
                    is the primary lens during review meetings. */}
                <Select value={custRegionFilter} onValueChange={setCustRegionFilter}>
                  <SelectTrigger className="w-auto min-w-[110px] h-9 text-xs border-0 bg-gray-50 hover:bg-gray-100 rounded-lg">
                    <span className="flex items-center gap-1">
                      <MapPin size={12} className="text-muted-foreground" />
                      <SelectValue />
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Regions</SelectItem>
                    {customerRegionOptions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={custEmployeeFilter} onValueChange={setCustEmployeeFilter}>
                  <SelectTrigger className="w-auto min-w-[100px] h-9 text-xs border-0 bg-gray-50 hover:bg-gray-100 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Staff</SelectItem>
                    {employeeNames.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select
                  value={`${custSort.field}:${custSort.dir}`}
                  onValueChange={(v) => { const [field, dir] = v.split(':'); setCustSort({ field, dir }); }}
                >
                  <SelectTrigger className="w-auto min-w-[150px] h-9 text-xs border-0 bg-gray-50 hover:bg-gray-100 rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dispatch_pct:desc">Achievement: High → Low</SelectItem>
                    <SelectItem value="dispatch_pct:asc">Achievement: Low → High</SelectItem>
                    <SelectItem value="dispatch_value:desc">Sales: High → Low</SelectItem>
                    <SelectItem value="name:asc">Name: A → Z</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-[10px] text-muted-foreground bg-gray-100 px-2 py-1 rounded-full">{filteredCustomers.length}</span>
                {/* PDF downloads are restricted to admin + accounts for data integrity. */}
                {canEditTargets && (
                  <Button
                    onClick={downloadCustomerReport}
                    disabled={downloadingReport || customerData.length === 0}
                    size="sm"
                    variant="outline"
                    className="gap-1 h-8 text-xs"
                    title={`Download the full FY ${targetsFY} target vs dispatch report for every ${activeDivision} customer`}
                  >
                    <Download size={12} />
                    {downloadingReport ? 'Generating...' : 'Download PDF'}
                  </Button>
                )}
                {canEditTargets && (
                  <Button
                    onClick={saveCustomerTargets}
                    disabled={savingCustTargets || filteredCustomers.length === 0}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 gap-1 h-8 text-xs"
                    title={`Saves targets for the ${filteredCustomers.length} customer(s) currently on the page`}
                  >
                    <Save size={12} />
                    {savingCustTargets ? 'Saving...' : visibleDirtyCount > 0 ? `Save (${visibleDirtyCount})` : 'Save'}
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {activeDivision === 'TAS' && !loadingCustomers && (
              <div className="flex items-center flex-wrap gap-2 mb-3 text-[10px] text-muted-foreground">
                <span className="font-semibold uppercase tracking-wide">Achievement</span>
                <span className="px-2 py-0.5 rounded bg-green-500 text-white font-semibold">100%+</span>
                <span className="px-2 py-0.5 rounded bg-lime-300 text-lime-900 font-semibold">80–99%</span>
                <span className="px-2 py-0.5 rounded bg-amber-300 text-amber-900 font-semibold">60–79%</span>
                <span className="px-2 py-0.5 rounded bg-red-400 text-white font-semibold">&lt;60%</span>
                <span className="px-2 py-0.5 rounded border border-dashed border-gray-300">Upcoming (uncoloured)</span>
              </div>
            )}
            {loadingCustomers ? (
              <p className="text-sm text-muted-foreground py-8 text-center">Loading...</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    {/* TAS — two-row header: 4 quarter group labels on
                        top, Target/Actual/% on each. Annual total at the
                        right. Non-TAS keeps the single-row header. */}
                    {activeDivision === 'TAS' ? (
                      <>
                        <tr className="bg-gray-50 border-b">
                          <th rowSpan="2" className="text-left p-2 font-semibold align-bottom cursor-pointer hover:text-blue-600" onClick={() => toggleSort('name')}>
                            Customer {custSort.field === 'name' && (custSort.dir === 'asc' ? '\u2191' : '\u2193')}
                          </th>
                          <th rowSpan="2" className="text-left p-2 font-semibold w-[70px] align-bottom">Region</th>
                          <th rowSpan="2" className="text-left p-2 font-semibold w-[100px] align-bottom">Assigned To</th>
                          <th rowSpan="2" className="text-center p-2 font-semibold w-[80px] align-bottom">Status</th>
                          {['Q1', 'Q2', 'Q3', 'Q4'].map(q => (
                            <th key={q} colSpan="3" className="text-center p-1 font-semibold text-indigo-600 border-l">{q}</th>
                          ))}
                          <th colSpan="3" className="text-center p-1 font-semibold text-purple-700 border-l bg-purple-50/40">Annual</th>
                        </tr>
                        <tr className="bg-gray-50 border-b">
                          {['Q1', 'Q2', 'Q3', 'Q4'].map(q => (
                            <React.Fragment key={q}>
                              {canViewTargets && <th className="text-right p-1 font-semibold w-[96px] text-green-600 border-l">Target</th>}
                              <th className="text-right p-1 font-semibold w-[80px]">Actual</th>
                              <th className="text-right p-1 font-semibold w-[45px] text-green-600">%</th>
                            </React.Fragment>
                          ))}
                          {canViewTargets && <th className="text-right p-1 font-semibold w-[96px] text-green-700 border-l bg-purple-50/40">Target</th>}
                          <th className="text-right p-1 font-semibold w-[80px] bg-purple-50/40">Actual</th>
                          <th className="text-right p-1 font-semibold w-[45px] text-green-700 bg-purple-50/40 cursor-pointer hover:text-blue-600" onClick={() => toggleSort('dispatch_pct')} title="Sort by achievement">
                            % {custSort.field === 'dispatch_pct' && (custSort.dir === 'asc' ? '\u2191' : '\u2193')}
                          </th>
                        </tr>
                      </>
                    ) : (
                      <tr className="bg-gray-50 border-b">
                        <th className="text-left p-2 font-semibold cursor-pointer hover:text-blue-600" onClick={() => toggleSort('name')}>
                          Customer {custSort.field === 'name' && (custSort.dir === 'asc' ? '\u2191' : '\u2193')}
                        </th>
                        <th className="text-left p-2 font-semibold w-[70px]">Region</th>
                        <th className="text-left p-2 font-semibold w-[100px]">Assigned To</th>
                        <th className="text-center p-2 font-semibold w-[80px]">Status</th>
                        {canViewTargets && <th className="text-right p-2 font-semibold w-[120px] text-green-600">Sales Target (₹)</th>}
                        <th className="text-right p-2 font-semibold cursor-pointer hover:text-blue-600" onClick={() => toggleSort('dispatch_value')}>
                          Sales Actual (₹) {custSort.field === 'dispatch_value' && (custSort.dir === 'asc' ? '\u2191' : '\u2193')}
                        </th>
                        <th className="text-right p-2 font-semibold w-[50px] text-green-600 cursor-pointer hover:text-blue-600" onClick={() => toggleSort('dispatch_pct')} title="Sort by achievement">
                          % {custSort.field === 'dispatch_pct' && (custSort.dir === 'asc' ? '\u2191' : '\u2193')}
                        </th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {filteredCustomers.map((c, idx) => {
                      // Find original index for editing — match by unique customer_id
                      // (falling back to customer_code) so duplicate-named records don't
                      // cause edits to land on the wrong row.
                      const origIdx = customerData.findIndex(x =>
                        (c.customer_id && x.customer_id === c.customer_id) ||
                        (c.customer_code && x.customer_code === c.customer_code)
                      );
                      const isCustExpanded = expandedCustomer === c.name;
                      return (
                        <React.Fragment key={c.customer_id || c.customer_code || c.name}>
                        <tr className={`border-b hover:bg-gray-50 ${isCustExpanded ? 'bg-indigo-50/50' : ''}`}>
                          {/* Only the name cell toggles expansion — editing inputs/dropdowns stays put */}
                          <td className="p-2 cursor-pointer" onClick={() => setExpandedCustomer(isCustExpanded ? null : c.name)}>
                            <div className="flex items-center gap-1">
                              {isCustExpanded ? <ChevronDown size={12} className="text-indigo-500 flex-shrink-0" /> : <ChevronRight size={12} className="text-muted-foreground flex-shrink-0" />}
                              <div className="flex-1">
                                <div className="font-medium text-xs">{c.name}</div>
                                {c.customer_code && <span className="text-[10px] text-muted-foreground">{c.customer_code}</span>}
                                {c.location && <span className="text-[10px] text-sky-700 font-medium">{c.customer_code ? ' · ' : ''}{c.location}</span>}
                              </div>
                              {/* Store chevron (TAS) — unfolds one row per
                                  ship-to location under this code. */}
                              {activeDivision === 'TAS' && Array.isArray(c.stores) && c.stores.length > 0 && (() => {
                                const key = c.customer_code || c.name;
                                const open = expandedStores.has(key);
                                return (
                                  <button
                                    type="button"
                                    title={open ? 'Hide stores' : `Show ${c.stores.length} store${c.stores.length === 1 ? '' : 's'}`}
                                    onClick={(e) => { e.stopPropagation(); setExpandedStores(prev => { const s = new Set(prev); if (s.has(key)) s.delete(key); else s.add(key); return s; }); }}
                                    className={`flex-shrink-0 inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] border ${open ? 'bg-amber-100 border-amber-300 text-amber-800' : 'bg-white border-amber-200 text-amber-700 hover:bg-amber-50'}`}
                                  >
                                    <Store size={10} />{c.stores.length}
                                    {open ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                                  </button>
                                );
                              })()}
                              {canEditTargets && (
                                <button
                                  type="button"
                                  title="Download this customer's target vs dispatch statement"
                                  onClick={(e) => { e.stopPropagation(); downloadSingleCustomerReport(c); }}
                                  disabled={downloadingCustomerId === (c.customer_id || c.customer_code || c.name)}
                                  className="flex-shrink-0 p-1 rounded text-muted-foreground hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-50"
                                >
                                  <Download size={13} className={downloadingCustomerId === (c.customer_id || c.customer_code || c.name) ? 'animate-pulse' : ''} />
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="p-2 text-xs text-muted-foreground">{c.region || '-'}</td>
                          <td className="p-2 text-xs">
                            <span className={c.assigned_employee_name === 'Head Office' ? 'text-purple-600 font-medium' : 'text-gray-700'}>
                              {c.assigned_employee_name || 'Head Office'}
                            </span>
                          </td>
                          <td className="p-2 text-center" onClick={e => e.stopPropagation()}>
                            {isAdmin && c.customer_id ? (
                              <Select value={c.status || 'active'} onValueChange={(val) => updateCustomerStatus(c.customer_id, val)}>
                                <SelectTrigger className={`h-6 text-[10px] w-[75px] mx-auto border ${STATUS_COLORS[c.status || 'active']}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="active">Active</SelectItem>
                                  <SelectItem value="dormant">Dormant</SelectItem>
                                  <SelectItem value="blocked">Blocked</SelectItem>
                                  <SelectItem value="closed">Closed</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className={`inline-block px-2 py-0.5 rounded text-[10px] border ${STATUS_COLORS[c.status || 'active']}`}>{c.status || 'active'}</span>
                            )}
                          </td>
                          {/* Targets are READ-ONLY ₹ text in the table.
                              Editing happens via the pencil button on the
                              last column → opens a dedicated dialog. This
                              prevents accidental wheel-scroll / arrow-key
                              edits that would silently change incentive
                              numbers. */}
                          {activeDivision === 'TAS' ? (
                            <>
                              {['Q1', 'Q2', 'Q3', 'Q4'].map(qName => {
                                const qActual = Array.isArray(c.quarterly)
                                  ? (c.quarterly.find(x => (x.quarter || '').toUpperCase() === qName) || {})
                                  : {};
                                const qTarget = getQuarterTarget(c, qName);
                                // Upcoming quarters stay neutral; current +
                                // past quarters get the achievement heat.
                                const future = isFutureQuarter(qName);
                                const heat = future ? null : heatBg(qActual.dispatch_pct);
                                return (
                                  <React.Fragment key={qName}>
                                    {canViewTargets && (
                                      <td className={`p-2 text-right border-l ${heat ? heat.soft : ''}`}>
                                        <span className="text-xs font-medium text-foreground tabular-nums whitespace-nowrap">
                                          {qTarget > 0 ? fmtNum(qTarget) : '-'}
                                        </span>
                                      </td>
                                    )}
                                    <td className={`p-2 text-right font-semibold ${heat ? heat.soft : ''}`}>
                                      {(qActual.dispatch_value || 0) > 0 ? fmtNum(qActual.dispatch_value) : '-'}
                                    </td>
                                    <td className={`p-2 text-right font-bold ${heat && heat.strong ? heat.strong : pctColor(qActual.dispatch_pct)}`}>
                                      {qActual.dispatch_pct != null ? `${qActual.dispatch_pct}%` : '-'}
                                    </td>
                                  </React.Fragment>
                                );
                              })}
                              {/* Annual column — read-only, auto-summed.
                                  Pencil button lives at the end so the
                                  click target is consistent + obvious. */}
                              {canViewTargets && (
                                <td className="p-2 text-right border-l bg-purple-50/30">
                                  <div className="flex items-center justify-end gap-1">
                                    <span className="text-xs font-semibold text-purple-900 tabular-nums whitespace-nowrap">
                                      {fmtNum(c.target_dispatch_value || 0)}
                                    </span>
                                    {canEditTargets && (
                                      <button
                                        type="button"
                                        title="Edit quarterly targets"
                                        className="p-0.5 rounded hover:bg-purple-200 text-purple-700 transition"
                                        onClick={e => { e.stopPropagation(); openTargetEditor(origIdx); }}
                                      >
                                        <Edit3 size={11} />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              )}
                              <td className={`p-2 text-right font-semibold ${heatBg(c.dispatch_pct).soft || 'bg-purple-50/30'}`}>
                                {c.dispatch_value > 0 ? `₹${fmtNum(c.dispatch_value)}` : '-'}
                              </td>
                              <td className={`p-2 text-right font-bold ${heatBg(c.dispatch_pct).strong || `bg-purple-50/30 ${pctColor(c.dispatch_pct)}`}`}>
                                {c.dispatch_pct != null ? `${c.dispatch_pct}%` : '-'}
                              </td>
                            </>
                          ) : (
                            <>
                              {canViewTargets && (
                                <td className="p-2 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <span className="text-xs font-medium text-foreground tabular-nums whitespace-nowrap">
                                      {fmtNum(c.target_dispatch_value || 0) || '-'}
                                    </span>
                                    {canEditTargets && (
                                      <button
                                        type="button"
                                        title="Edit annual target"
                                        className="p-0.5 rounded hover:bg-gray-200 text-gray-600 transition"
                                        onClick={e => { e.stopPropagation(); openTargetEditor(origIdx); }}
                                      >
                                        <Edit3 size={11} />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              )}
                              <td className="p-2 text-right font-semibold">{c.dispatch_value > 0 ? `₹${fmtNum(c.dispatch_value)}` : '-'}</td>
                              <td className={`p-2 text-right font-bold ${pctColor(c.dispatch_pct)}`}>{c.dispatch_pct != null ? `${c.dispatch_pct}%` : '-'}</td>
                            </>
                          )}
                        </tr>
                        {/* Expanded monthly breakdown for this customer */}
                        {isCustExpanded && c.monthly && (
                          <tr>
                            <td
                              colSpan={
                                activeDivision === 'TAS'
                                  ? 4 /* customer/region/assigned/status */
                                    + 4 * (canViewTargets ? 3 : 2) /* 4 quarter groups, 2 or 3 cells each */
                                    + (canViewTargets ? 3 : 2) /* annual group */
                                  : (canViewTargets ? 7 : 6)
                              }
                              className="p-0"
                            >
                              <div className="bg-gray-50 border-y px-4 py-2">
                                {/* Monthly breakdown drill-down — useful
                                    for spotting which months within a
                                    quarter are pulling weight. The
                                    quarter columns up top already show
                                    quarter-level performance. */}
                                <p className="text-[10px] font-bold text-indigo-600 uppercase mb-1.5">Monthly Breakdown — {c.name}</p>
                                <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-1.5">
                                  {c.monthly.map((m, mi) => (
                                    <div key={m.period} className={`text-center rounded-lg p-1.5 ${m.dispatch_value > 0 ? 'bg-white border' : 'bg-gray-100'}`}>
                                      <p className="text-[9px] font-semibold text-muted-foreground">{MONTH_LABELS[mi]}</p>
                                      <p className={`text-xs font-bold mt-0.5 ${m.dispatch_value > 0 ? 'text-foreground' : 'text-gray-400'}`}>{m.dispatch_value > 0 ? fmtNum(m.dispatch_value) : '-'}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                        {/* Store rows (TAS) — one per ship-to location, same
                            column grid as the customer row: Q1–Q4 target |
                            actual | %, then annual. The customer row above
                            is untouched; these are a view underneath it. */}
                        {activeDivision === 'TAS' && Array.isArray(c.stores) && c.stores.length > 0 && expandedStores.has(c.customer_code || c.name) && c.stores.map(s => {
                          const sq = (qName) => (Array.isArray(s.quarterly) ? s.quarterly.find(x => (x.quarter || '').toUpperCase() === qName) : null) || {};
                          return (
                            <tr key={`${c.customer_code || c.name}-store-${s.ship_to_code || 'none'}`} className="border-b bg-amber-50/40 text-[11px]">
                              <td className="p-2 pl-7">
                                <div className="flex items-center gap-1.5">
                                  <Store size={10} className="text-amber-600 flex-shrink-0" />
                                  <span className={`${s.ship_to_code ? 'text-foreground' : 'text-muted-foreground italic'}`}>{s.name || (s.ship_to_code ? s.ship_to_code : 'No store')}</span>
                                  {s.is_head && <span className="text-[9px] px-1 rounded border border-amber-300 text-amber-800 bg-amber-50">head</span>}
                                  {s.ship_to_code && !s.is_head && <span className="text-[9px] text-muted-foreground">{s.ship_to_code}</span>}
                                </div>
                              </td>
                              <td className="p-2"></td>
                              <td className="p-2"></td>
                              <td className="p-2"></td>
                              {['Q1', 'Q2', 'Q3', 'Q4'].map(qName => {
                                const q = sq(qName);
                                const heat = isFutureQuarter(qName) ? null : heatBg(q.dispatch_pct);
                                return (
                                  <React.Fragment key={qName}>
                                    {canViewTargets && (
                                      <td className={`p-2 text-right border-l tabular-nums ${heat ? heat.soft : ''}`}>{(q.target_dispatch_value || 0) > 0 ? fmtNum(q.target_dispatch_value) : '-'}</td>
                                    )}
                                    <td className={`p-2 text-right font-medium ${heat ? heat.soft : ''}`}>{(q.dispatch_value || 0) > 0 ? fmtNum(q.dispatch_value) : '-'}</td>
                                    <td className={`p-2 text-right font-semibold ${heat && heat.strong ? heat.strong : pctColor(q.dispatch_pct)}`}>{q.dispatch_pct != null ? `${q.dispatch_pct}%` : '-'}</td>
                                  </React.Fragment>
                                );
                              })}
                              {canViewTargets && (
                                <td className="p-2 text-right border-l bg-purple-50/30 tabular-nums">{(s.target_dispatch_value || 0) > 0 ? fmtNum(s.target_dispatch_value) : '-'}</td>
                              )}
                              <td className={`p-2 text-right font-medium ${heatBg(s.dispatch_pct).soft || 'bg-purple-50/30'}`}>{(s.dispatch_value || 0) > 0 ? `₹${fmtNum(s.dispatch_value)}` : '-'}</td>
                              <td className={`p-2 text-right font-semibold ${heatBg(s.dispatch_pct).strong || `bg-purple-50/30 ${pctColor(s.dispatch_pct)}`}`}>{s.dispatch_pct != null ? `${s.dispatch_pct}%` : '-'}</td>
                            </tr>
                          );
                        })}
                        </React.Fragment>
                      );
                    })}
                    <tr className="border-t-2 bg-gray-100 font-bold text-xs">
                      <td className="p-2">Total ({filteredCustomers.length})</td>
                      <td className="p-2"></td>
                      <td className="p-2"></td>
                      <td className="p-2"></td>
                      {activeDivision === 'TAS' ? (
                        <>
                          {['Q1', 'Q2', 'Q3', 'Q4'].map(q => {
                            const qt = tasQuarterTotals[q];
                            const pct = qt.target > 0 ? Math.round(qt.actual / qt.target * 100) : null;
                            return (
                              <React.Fragment key={q}>
                                {canViewTargets && <td className="p-2 text-right border-l">{fmtNum(qt.target)}</td>}
                                <td className="p-2 text-right">{fmtNum(qt.actual)}</td>
                                <td className="p-2 text-right">{pct != null ? `${pct}%` : '-'}</td>
                              </React.Fragment>
                            );
                          })}
                          {canViewTargets && (
                            <td className="p-2 text-right border-l bg-purple-50/40">{fmtNum(custTotals.target_dispatch_value)}</td>
                          )}
                          <td className="p-2 text-right bg-purple-50/40">{fmtNum(custTotals.dispatch_value)}</td>
                          <td className="p-2 text-right bg-purple-50/40">
                            {custTotals.target_dispatch_value > 0 ? `${Math.round(custTotals.dispatch_value / custTotals.target_dispatch_value * 100)}%` : '-'}
                          </td>
                        </>
                      ) : (
                        <>
                          {canViewTargets && <td className="p-2 text-right">{fmtNum(custTotals.target_dispatch_value)}</td>}
                          <td className="p-2 text-right">{fmtNum(custTotals.dispatch_value)}</td>
                          <td className="p-2 text-right">{custTotals.target_dispatch_value > 0 ? `${Math.round(custTotals.dispatch_value / custTotals.target_dispatch_value * 100)}%` : '-'}</td>
                        </>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Floating save FAB — stays visible while scrolling a long customer list */}
      {viewMode === 'customers' && canEditTargets && visibleDirtyCount > 0 && (
        <div className="fixed bottom-6 right-6 z-50">
          <Button
            onClick={saveCustomerTargets}
            disabled={savingCustTargets}
            className="bg-green-600 hover:bg-green-700 shadow-lg gap-2 h-11 px-4 rounded-full"
            title={`Saves targets for the ${filteredCustomers.length} customer(s) currently on the page`}
          >
            <Save size={16} />
            {savingCustTargets
              ? 'Saving...'
              : `Save ${visibleDirtyCount} edit${visibleDirtyCount === 1 ? '' : 's'} on page`}
          </Button>
        </div>
      )}

      {/* ── Edit Target dialog ─────────────────────────────────
          Forced explicit entry so admin can't change a target via
          accidental mouse-wheel scroll on a focused input. Read-only
          inline cells + this dialog = sacrosanct numbers. */}
      <Dialog open={targetEditOpen} onOpenChange={setTargetEditOpen}>
        <DialogContent className={storeEditDraft ? 'sm:max-w-2xl max-h-[92vh] overflow-y-auto' : 'sm:max-w-md'}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Target size={16} className="text-indigo-600" />
              Edit Sales Target
            </DialogTitle>
            {targetEditRowIdx >= 0 && customerData[targetEditRowIdx] && (
              <p className="text-xs text-muted-foreground pt-1">
                <span className="font-semibold text-foreground">{customerData[targetEditRowIdx].name}</span>
                {customerData[targetEditRowIdx].customer_code && (
                  <span className="text-[10px] ml-2">({customerData[targetEditRowIdx].customer_code})</span>
                )}
                <span className="ml-2 text-[10px]">· {activeDivision} · {targetsFY}</span>
              </p>
            )}
          </DialogHeader>

          {activeDivision === 'TAS' ? (
            <div className="space-y-3 py-2">
              <p className="text-[11px] text-muted-foreground">
                Principals issue TAS targets quarterly. Annual total below is auto-summed.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {['Q1', 'Q2', 'Q3', 'Q4'].map(q => (
                  <div key={q}>
                    <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
                      {q} <span className="text-[9px] font-normal">
                        {q === 'Q1' ? '(Apr–Jun)' : q === 'Q2' ? '(Jul–Sep)' : q === 'Q3' ? '(Oct–Dec)' : '(Jan–Mar)'}
                      </span>
                    </Label>
                    <Input
                      type="number"
                      className="text-right h-9 mt-1"
                      value={targetEditDraft.quarters[q] || ''}
                      onChange={e => setTargetEditDraft(d => ({
                        ...d,
                        quarters: { ...d.quarters, [q]: parseFloat(e.target.value) || 0 },
                      }))}
                      placeholder="0"
                    />
                  </div>
                ))}
              </div>
              <div className="border-t pt-2 mt-2 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Annual total</span>
                <span className="text-base font-bold text-indigo-700">
                  ₹{fmtNum(['Q1', 'Q2', 'Q3', 'Q4'].reduce((s, q) => s + (targetEditDraft.quarters[q] || 0), 0))}
                </span>
              </div>

              {/* By store — only for codes with ≥ 2 ship-to locations (or
                  store targets already on file). Σ stores is checked live
                  against the code figures above; amber when off by > ₹1. */}
              {storeEditDraft && targetEditRowIdx >= 0 && (() => {
                const stores = targetableStores(customerData[targetEditRowIdx]);
                const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
                const offQ = quarters.filter(q => Math.abs(storeDraftSum(q) - (targetEditDraft.quarters[q] || 0)) > 1);
                return (
                  <div className="border-t pt-3 mt-2 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Label className="text-[11px] font-semibold uppercase tracking-wide text-amber-800 flex items-center gap-1">
                        <Store size={12} /> By store
                      </Label>
                      <Button type="button" variant="outline" size="sm" className="h-7 text-[11px] border-amber-300 text-amber-800 hover:bg-amber-50"
                        onClick={splitStoresByShare} title="Divide each quarter across the stores in proportion to their dispatches this FY so far (equal split when none)">
                        Split by last period's share
                      </Button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px] border-collapse">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
                            <th className="text-left pb-1 pr-2">Store</th>
                            {quarters.map(q => <th key={q} className="text-right pb-1 px-1 w-[88px]">{q}</th>)}
                            <th className="text-right pb-1 pl-1 w-[80px]">Annual</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stores.map(s => (
                            <tr key={s.ship_to_code} className="border-t">
                              <td className="py-1 pr-2">
                                <div className="truncate max-w-[150px]" title={`${s.name || s.ship_to_code} (${s.ship_to_code})`}>
                                  {s.name || s.ship_to_code}{s.is_head && <span className="ml-1 text-[9px] text-amber-700">head</span>}
                                </div>
                                {(s.dispatch_value || 0) > 0 && <div className="text-[9px] text-muted-foreground">this FY ₹{fmtNum(s.dispatch_value)}</div>}
                              </td>
                              {quarters.map(q => (
                                <td key={q} className="py-1 px-1">
                                  <Input
                                    type="number"
                                    min={0}
                                    className="h-7 text-right text-[11px] px-1.5"
                                    value={storeEditDraft[s.ship_to_code]?.[q] || ''}
                                    onChange={e => {
                                      const v = parseFloat(e.target.value) || 0;
                                      setStoreEditDraft(d => ({ ...d, [s.ship_to_code]: { ...(d?.[s.ship_to_code] || {}), [q]: v } }));
                                    }}
                                    placeholder="0"
                                  />
                                </td>
                              ))}
                              <td className="py-1 pl-1 text-right tabular-nums font-medium">
                                {fmtNum(quarters.reduce((t, q) => t + (storeEditDraft[s.ship_to_code]?.[q] || 0), 0))}
                              </td>
                            </tr>
                          ))}
                          <tr className="border-t-2 font-semibold">
                            <td className="py-1 pr-2 text-muted-foreground">Σ stores</td>
                            {quarters.map(q => {
                              const off = offQ.includes(q);
                              return (
                                <td key={q} className={`py-1 px-1 text-right tabular-nums ${off ? 'text-amber-700' : 'text-green-700'}`}
                                  title={off ? `Stores add to ${fmtNum(storeDraftSum(q))} but the code figure above is ${fmtNum(targetEditDraft.quarters[q] || 0)}` : 'Matches the code figure above'}>
                                  {fmtNum(storeDraftSum(q))}
                                </td>
                              );
                            })}
                            <td className="py-1 pl-1 text-right tabular-nums">{fmtNum(quarters.reduce((t, q) => t + storeDraftSum(q), 0))}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    {storeDraftHasFigures() ? (
                      offQ.length > 0 ? (
                        <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                          Σ stores differs from the code figure in {offQ.join(', ')}. Saving store targets makes the code target equal to the store total.
                        </p>
                      ) : (
                        <p className="text-[10px] text-green-700">Store targets add up to the code figures.</p>
                      )
                    ) : (
                      <p className="text-[10px] text-muted-foreground">No store figures — the code target is kept as a single figure. Fill the stores (or use Split) to set store targets.</p>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
                  Annual Sales Target (₹)
                </Label>
                <Input
                  type="number"
                  className="text-right h-9 mt-1"
                  value={targetEditDraft.annual || ''}
                  onChange={e => setTargetEditDraft(d => ({ ...d, annual: parseFloat(e.target.value) || 0 }))}
                  placeholder="0"
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setTargetEditOpen(false)} disabled={savingStoreTargets}>Cancel</Button>
            {/* With store figures the dialog saves straight to the server
                (the code target becomes their sum); otherwise today's
                apply-then-Save-on-page flow is unchanged. */}
            {storeEditDraft && !storeDraftHasFigures() && targetEditRowIdx >= 0 && storeTargetsOnFile(customerData[targetEditRowIdx] || {}) && (
              <Button variant="outline" onClick={() => { if (window.confirm('Remove the store targets for this code? The code target stays as it is.')) saveStoreTargets(); }}
                disabled={savingStoreTargets} className="text-amber-700 border-amber-300 hover:bg-amber-50">
                {savingStoreTargets ? 'Clearing…' : 'Clear store targets'}
              </Button>
            )}
            {storeEditDraft && storeDraftHasFigures() ? (
              <Button onClick={saveStoreTargets} disabled={savingStoreTargets} className="bg-amber-600 hover:bg-amber-700">
                <Save size={14} className="mr-1" />
                {savingStoreTargets ? 'Saving…' : 'Save store targets'}
              </Button>
            ) : (
              <Button onClick={commitTargetEdit} className="bg-indigo-600 hover:bg-indigo-700">
                Apply changes
              </Button>
            )}
          </DialogFooter>
          <p className="text-[10px] text-muted-foreground text-center -mt-1">
            {storeEditDraft && storeDraftHasFigures()
              ? 'Store targets are saved immediately and set the code target to their sum.'
              : <>Apply queues the change. Click <span className="font-semibold">Save</span> on the page to persist.</>}
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
